import { useState, useEffect, createContext, useContext } from 'react';
import { useParams, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui';
import { 
  ArrowLeft, Activity, Monitor, Puzzle, Zap, Radio, Cog, MessagesSquare, RefreshCw, AlertCircle,
  Pause, Play, Settings, Trash2, Power, Globe, Copy, Check, ExternalLink
} from 'lucide-react';
import { supabase } from '../../lib/supabase/client';
import { workerFetch } from '../../lib/worker-api';
import { Button, Card } from '../ui';
import { useToastHelper } from '../../hooks/useToastHelper';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '../ui/alert-dialog';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

type AgentStatus = 'active' | 'provisioning' | 'configuring' | 'paused' | 'stopped' | 'error' | 'deleted';

const statusColors: Record<AgentStatus, 'success' | 'warning' | 'default' | 'error'> = {
  active: 'success',
  provisioning: 'warning',
  configuring: 'warning',
  paused: 'default',
  stopped: 'default',
  error: 'error',
  deleted: 'error',
};

const roleEmojis: Record<string, string> = {
  'executive-assistant': '💼',
  'social-media-manager': '📊',
  'content-creator': '📝',
  'research-analyst': '🔬',
  'email-marketer': '📧',
  'lead-generator': '🎯',
  'dev-assistant': '💻',
  'trader': '📈',
  'customer-support': '💬',
  'sales-assistant': '📞',
  'project-manager': '📋',
  'data-analyst': '📈',
  'custom': '🎯',
};

interface AgentContextType {
  agent: any | null;
  isLoading: boolean;
  refetchAgent: () => Promise<void>;
}

const AgentContext = createContext<AgentContextType>({
  agent: null,
  isLoading: true,
  refetchAgent: async () => {},
});

export const useAgentContext = () => useContext(AgentContext);

export function AgentLayout() {
  const { t } = useTranslation();
  const { toast } = useToastHelper();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [chromeDialogOpen, setChromeDialogOpen] = useState(false);
  const [chromeEmail, setChromeEmail] = useState('');
  const [chromePassword, setChromePassword] = useState('');

  const fetchAgent = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      setAgent(data);
    } catch (err) {
      console.error('Failed to fetch agent:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchAgent();
  }, [id]);

  const handlePauseResume = async () => {
    if (!agent) return;
    setActionLoading('pause');
    try {
      const newStatus = agent.status === 'paused' ? 'active' : 'paused';
      if (newStatus === 'paused') {
        await workerFetch(`/agent/${agent.id}/stop`, { method: 'POST' });
      } else {
        await workerFetch(`/agent/${agent.id}/restart`, { method: 'POST' });
      }
      const { error: updateError } = await (supabase.from('agents') as any).update({ status: newStatus }).eq('id', agent.id);
      if (updateError) throw updateError;
      setAgent({ ...agent, status: newStatus });
    } catch (err) { console.error('Failed to update agent:', err); }
    finally { setActionLoading(null); }
  };

  const handleRestartGateway = async () => {
    if (!agent) return;
    setShowRestartDialog(false);
    setActionLoading('restart');
    try {
      await workerFetch(`/agent/${agent.id}/restart`, { method: 'POST' });
      toast({ title: t('agentDetail.restartSuccess') });
    } catch (err: any) {
      toast({ title: t('agentDetail.error'), description: err.message, variant: 'destructive' });
    } finally { setActionLoading(null); }
  };

  const handleDeleteConfirmed = async () => {
    if (!agent) return;
    setShowDeleteDialog(false);
    setDeleteConfirmText('');
    setActionLoading('delete');
    try {
      await workerFetch(`/agent/${agent.id}/terminate`, { method: 'POST' });
      const { error: deleteError } = await (supabase.from('agents') as any).update({ status: 'deleted', deleted_at: new Date().toISOString() }).eq('id', agent.id);
      if (deleteError) throw deleteError;
      navigate('/dashboard');
    } catch (err) { console.error('Failed to delete agent:', err); }
    finally { setActionLoading(null); }
  };

  const handleTerminate = async () => {
    if (!agent) return;
    setShowTerminateDialog(false);
    setActionLoading('terminate');
    try {
      await workerFetch(`/agent/${agent.id}/terminate`, { method: 'POST' });
      toast({ title: t('agentDetail.terminateSuccess') });
      navigate('/dashboard');
    } catch (err: any) {
      toast({ title: t('agentDetail.error'), description: err.message, variant: 'destructive' });
    } finally { setActionLoading(null); }
  };

  const handleChromeSetup = async () => {
    if (!agent || !chromeEmail) return;
    setActionLoading('chrome-setup');
    try {
      await workerFetch(`/agent/${agent.id}/chrome-setup`, {
        method: 'POST',
        body: JSON.stringify({ google_email: chromeEmail, google_password: chromePassword, agent_id: agent.id }),
      });
      toast({ title: t('agentDetail.chromeSetupSuccess') });
      setChromeDialogOpen(false);
      setChromeEmail('');
      setChromePassword('');
    } catch (err: any) {
      toast({ title: t('agentDetail.error'), description: err.message, variant: 'destructive' });
    } finally { setActionLoading(null); }
  };

  const tabs = [
    { key: 'overview', label: t('agentNav.overview'), to: `/dashboard/agent/${id}`, icon: Activity },
    { key: 'live', label: t('agentNav.live'), to: `/dashboard/agent/${id}/live`, icon: Monitor },
    { key: 'automations', label: t('agentNav.automations'), to: `/dashboard/agent/${id}/automations`, icon: Zap },
    { key: 'settings', label: t('agentNav.settings'), to: `/dashboard/agent/${id}/settings`, icon: Cog },
  ];

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.endsWith('/live')) return 'live';
    if (path.endsWith('/conversations')) return 'conversations';
    if (path.endsWith('/skills')) return 'skills';
    if (path.endsWith('/automations')) return 'automations';
    if (path.endsWith('/channels')) return 'channels';
    if (path.endsWith('/settings')) return 'settings';
    return 'overview';
  };

  const activeTab = getActiveTab();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Agente non trovato</h2>
          <p className="text-muted-foreground mb-6">L'agente richiesto non esiste o non hai i permessi per visualizzarlo.</p>
          <Link to="/dashboard">
            <Button variant="accent">Torna al Team</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <AgentContext.Provider value={{ agent, isLoading, refetchAgent: fetchAgent }}>
      <div className="min-h-screen bg-background">
        {/* Shared Header */}
        <div className="border-b border-border-subtle bg-card/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link 
              to="/dashboard" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-3 transition-colors text-sm"
            >
              <ArrowLeft size={14} />
              Torna al Team
            </Link>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-accent-secondary/20 flex items-center justify-center text-2xl">
                {roleEmojis[agent.role] || '🤖'}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-foreground">{agent.name}</h1>
                <p className="text-muted-foreground text-sm capitalize">{agent.role.replace(/-/g, ' ')}</p>
              </div>
              <Badge
                variant={statusColors[agent.status as AgentStatus]}
                dot={agent.status === 'active'}
                className="text-sm"
              >
                {t(`dashboard.status.${agent.status}`)}
              </Badge>
              {agent.subscription_status === 'active' && (
                <Badge variant="success" className="text-xs">Abbonato</Badge>
              )}
              {agent.subscription_status !== 'active' && agent.trial_expires_at && new Date(agent.trial_expires_at) > new Date() && (
                <Badge variant="warning" className="text-xs">Trial</Badge>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {agent.status === 'active' && (
                <Button variant="secondary" size="sm" onClick={handlePauseResume} isLoading={actionLoading === 'pause'}>
                  <Pause size={14} /> Metti in ferie
                </Button>
              )}
              {agent.status === 'paused' && (
                <Button variant="accent" size="sm" onClick={handlePauseResume} isLoading={actionLoading === 'pause'}>
                  <Play size={14} /> Richiama in servizio
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setShowRestartDialog(true)} isLoading={actionLoading === 'restart'}>
                <RefreshCw size={14} /> <span className="hidden sm:inline">Restart Gateway</span>
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setChromeDialogOpen(true)}>
                <Globe size={14} /> <span className="hidden sm:inline">Chrome</span>
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-t border-border-subtle bg-card/30">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="flex gap-1 overflow-x-auto no-scrollbar -mb-px">
                {tabs.map(tab => {
                  const isActive = tab.key === activeTab;
                  return (
                    <Link
                      key={tab.key}
                      to={tab.to}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        isActive
                          ? 'border-accent text-accent'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border-subtle'
                      }`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <Outlet />
      </div>

      {/* Restart Dialog */}
      <AlertDialog open={showRestartDialog} onOpenChange={setShowRestartDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('agentDetail.restartGateway')}</AlertDialogTitle>
            <AlertDialogDescription>{t('agentDetail.restartConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('agentDetail.cancel')}</AlertDialogCancel>
            <Button variant="accent" size="sm" onClick={handleRestartGateway}>Riavvia</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(o) => { setShowDeleteDialog(o); if (!o) setDeleteConfirmText(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('agentDetail.fireConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('agentDetail.fireConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Input placeholder={t('agentDetail.fireConfirmPlaceholder')} value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('agentDetail.cancel')}</AlertDialogCancel>
            <Button variant="ghost" size="sm" className="text-error hover:bg-error/10" onClick={handleDeleteConfirmed} disabled={deleteConfirmText !== t('agentDetail.fireConfirmWord')}>
              {t('agentDetail.fire')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Terminate Dialog */}
      <AlertDialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('agentDetail.terminateTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('agentDetail.terminateDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('agentDetail.cancel')}</AlertDialogCancel>
            <Button variant="ghost" size="sm" className="text-error hover:bg-error/10 border border-error/20" onClick={handleTerminate}>
              {t('agentDetail.terminate')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Chrome Setup Dialog */}
      <Dialog open={chromeDialogOpen} onOpenChange={setChromeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('agentDetail.chromeSetup.title')}</DialogTitle>
            <DialogDescription>{t('agentDetail.chromeSetup.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">{t('agentDetail.chromeSetup.email')}</label>
              <Input value={chromeEmail} onChange={e => setChromeEmail(e.target.value)} placeholder="email@gmail.com" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">{t('agentDetail.chromeSetup.password')}</label>
              <Input type="password" value={chromePassword} onChange={e => setChromePassword(e.target.value)} placeholder="••••••••" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={() => setChromeDialogOpen(false)}>{t('agentDetail.cancel')}</Button>
            <Button variant="accent" size="sm" onClick={handleChromeSetup} isLoading={actionLoading === 'chrome-setup'} disabled={!chromeEmail}>
              {t('agentDetail.chromeSetup.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentContext.Provider>
  );
}
